import React, {useEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {useMutation, useQuery} from "@apollo/client";
import {toast} from "react-toastify";
import useTranslation from "../../../i18n/useTranslation";
import useThemeColors from "../../organization/portal/useThemeColors";
import {DELETE_SOCCER_PLAYER_SCORE, SAVE_SOCCER_PLAYER_SCORE} from "../mutation/SaveSoccerPlayerScore";
import {GET_SOCCER_GAME_TIMELINE} from "../query/GetGameTimelineData";
import {GlobalStyles} from "../../../constants/styles";
import {getLocalizedGraphQLError} from "../../../util/graphqlError";
import GameMatchboard from "../GameMatchboard";

const EVENT_TYPES = [
    {key: "goal"},
    {key: "assist"},
    {key: "yellow_card"},
    {key: "red_card"},
    {key: "own_goal"},
    {key: "substitution_out"},
];

const PERIODS = [
    {key: "FIRST_HALF", value: "1st Half"},
    {key: "SECOND_HALF", value: "2nd Half"},
    {key: "EXTRA_TIME", value: "Extra Time"},
    {key: "PENALTIES", value: "Penalties"},
];

const INITIAL_FORM = {
    teamId: "",
    actionType: "",
    playerId: "",
    relatedPlayerId: "",
    minute: "",
    period: "",
    notes: "",
};

function getEventConfig(t) {
    return {
        goal: {
            icon: "fas fa-futbol",
            accent: "#12824a",
            soft: "rgba(18,130,74,0.12)",
            glow: "rgba(18,130,74,0.16)",
            label: t("gameElement.soccerGameSheet.eventTypes.goal"),
            shortLabel: "GOAL",
        },
        assist: {
            icon: "fas fa-hands-helping",
            accent: "#2563eb",
            soft: "rgba(37,99,235,0.12)",
            glow: "rgba(37,99,235,0.14)",
            label: t("gameElement.soccerGameSheet.eventTypes.assist"),
            shortLabel: "ASSIST",
        },
        substitution_in: {
            icon: "fas fa-arrow-up",
            accent: "#198754",
            soft: "rgba(25,135,84,0.10)",
            glow: "rgba(25,135,84,0.12)",
            label: t("gameElement.soccerGameSheet.eventTypes.substitution_in"),
            shortLabel: "IN",
        },
        substitution_out: {
            icon: "fas fa-arrow-down",
            accent: "#dc3545",
            soft: "rgba(220,53,69,0.10)",
            glow: "rgba(220,53,69,0.12)",
            label: t("gameElement.soccerGameSheet.eventTypes.substitution_out"),
            shortLabel: "SUB",
        },
        yellow_card: {
            icon: "fas fa-square",
            accent: "#c99700",
            soft: "rgba(255,215,0,0.18)",
            glow: "rgba(201,151,0,0.12)",
            label: t("gameElement.soccerGameSheet.eventTypes.yellow_card"),
            shortLabel: "YC",
        },
        red_card: {
            icon: "fas fa-square",
            accent: "#d92d20",
            soft: "rgba(217,45,32,0.12)",
            glow: "rgba(217,45,32,0.12)",
            label: t("gameElement.soccerGameSheet.eventTypes.red_card"),
            shortLabel: "RC",
        },
        own_goal: {
            icon: "fas fa-futbol",
            accent: "#a61e4d",
            soft: "rgba(166,30,77,0.10)",
            glow: "rgba(166,30,77,0.12)",
            label: t("gameElement.soccerGameSheet.eventTypes.own_goal"),
            shortLabel: "OG",
        },
    };
}

function TimelineEventCard({
                               event,
                               cfg,
                               canEdit,
                               onDelete,
                               side,
                               scopeSel,
                           }) {
    const actionKey = String(event?.actionType || "").toLowerCase();
    const isSubOut = actionKey === "substitution_out";
    const notes = String(event?.notes || "").trim();
    const colors = useThemeColors();

    return (
        <>
            <div
                className={`kbola-feedCard ${side === "right" ? "is-right" : "is-left"}`}
                style={{
                    "--evt-accent": cfg?.accent || "#14151a",
                    "--evt-soft": cfg?.soft || "rgba(0,0,0,0.06)",
                    "--evt-glow": cfg?.glow || "rgba(0,0,0,0.06)",
                }}
            >
                <div className="kbola-feedCardTop">
                    <div className="kbola-feedCardTopLeft">
                        <div className={`kbola-feedTeamTag ${side === "right" ? "is-right" : "is-left"}`}>
                            {side === "right" ? "HOME" : "AWAY"}
                        </div>

                        <div className="kbola-feedType">
                            <span className="kbola-feedTypeIcon">
                                <i className={`${cfg?.icon || "fas fa-circle"}`}/>
                            </span>
                            <span className="kbola-feedTypeText">
                                {cfg?.label || event?.actionType}
                            </span>
                        </div>
                    </div>

                    {canEdit && (
                        <button
                            type="button"
                            className="btn kbola-feedDelete"
                            onClick={() => onDelete(event.id)}
                            title="Delete"
                        >
                            <i className="fas fa-trash"/>
                        </button>
                    )}
                </div>

                {isSubOut ? (
                    <div className="kbola-feedBody">
                        <div className="kbola-feedPersonRow">
                            <span className="kbola-feedPersonIcon out">
                                <i className="fas fa-arrow-down"/>
                            </span>
                            <div className="kbola-feedPersonText">
                                <div className="kbola-feedPrimaryText">
                                    {event?.player?.fullName || "-"}
                                </div>
                                <div className="kbola-feedSecondaryText">Player out</div>
                            </div>
                        </div>

                        <div className="kbola-feedPersonRow">
                            <span className="kbola-feedPersonIcon in">
                                <i className="fas fa-arrow-up"/>
                            </span>
                            <div className="kbola-feedPersonText">
                                <div className="kbola-feedPrimaryText">
                                    {event?.relatedPlayer?.fullName || "-"}
                                </div>
                                <div className="kbola-feedSecondaryText">Player in</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="kbola-feedBody">
                        <div className="kbola-feedHeroRow">
                            <div className="kbola-feedHeroPlayer">
                                {event?.player?.fullName || "-"}
                            </div>

                            {cfg?.shortLabel ? (
                                <span className="kbola-feedMiniTag">
                                    {cfg.shortLabel}
                                </span>
                            ) : null}
                        </div>

                        {event?.relatedPlayer?.fullName ? (
                            <div className="kbola-feedLinked">
                                <i className="fas fa-link mr-2"/>
                                {event.relatedPlayer.fullName}
                            </div>
                        ) : null}
                    </div>
                )}

                {notes ? (
                    <div className="kbola-feedNotes">
                        <i className="far fa-sticky-note mr-2"/>
                        {notes}
                    </div>
                ) : null}
            </div>

            <style>{`
                ${scopeSel} .kbola-feedCard {
                    position: relative;
                    border-radius: 20px;
                    border: 1px solid rgba(0,0,0,0.07);
                    background:
                        linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%);
                    box-shadow:
                        0 12px 24px rgba(0,0,0,0.05),
                        0 2px 8px rgba(0,0,0,0.03);
                    padding: 14px 15px 14px;
                    overflow: hidden;
                }

                ${scopeSel} .kbola-feedCard::after {
                    content: "";
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    width: 5px;
                    background: var(--evt-accent);
                }

                ${scopeSel} .kbola-feedCard.is-left::after {
                    left: 0;
                }

                ${scopeSel} .kbola-feedCard.is-right::after {
                    right: 0;
                }

                ${scopeSel} .kbola-feedCardTop {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 10px;
                    margin-bottom: 12px;
                }

                ${scopeSel} .kbola-feedCardTopLeft {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    min-width: 0;
                }

                ${scopeSel} .kbola-feedTeamTag {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: fit-content;
                    min-height: 22px;
                    padding: 0 10px;
                    border-radius: 999px;
                    font-size: 10px;
                    font-weight: 900;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                    border: 1px solid rgba(0,0,0,0.08);
                    background: rgba(0,0,0,0.04);
                    color: rgba(20,21,26,0.7);
                    backdrop-filter: blur(4px);
                    white-space: nowrap;
                }

                ${scopeSel} .kbola-feedTeamTag.is-left {
                    background: ${colors.secondary};
                    color: ${colors.textOnSecondary};
                    border-color: ${colors.secondary};
                }

                ${scopeSel} .kbola-feedTeamTag.is-right {
                    background: ${colors.primary};
                    color: ${colors.textOnPrimary};
                    border-color: ${colors.primary};
                }

                ${scopeSel} .kbola-feedType {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    min-width: 0;
                }

                ${scopeSel} .kbola-feedTypeIcon {
                    width: 34px;
                    height: 34px;
                    border-radius: 12px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--evt-soft);
                    color: var(--evt-accent);
                    flex-shrink: 0;
                }

                ${scopeSel} .kbola-feedTypeText {
                    font-size: 13px;
                    font-weight: 900;
                    color: rgba(20,21,26,0.84);
                    line-height: 1.2;
                }

                ${scopeSel} .kbola-feedDelete {
                    width: 34px;
                    height: 34px;
                    padding: 0;
                    border-radius: 11px;
                    border: 1px solid rgba(220,53,69,0.18);
                    background: rgba(220,53,69,0.06);
                    color: rgba(220,53,69,0.95);
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                ${scopeSel} .kbola-feedDelete:hover {
                    background: rgba(220,53,69,0.10);
                }

                ${scopeSel} .kbola-feedBody {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                ${scopeSel} .kbola-feedHeroRow {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                ${scopeSel} .kbola-feedHeroPlayer {
                    font-size: 15px;
                    font-weight: 900;
                    color: rgba(20,21,26,0.95);
                    line-height: 1.25;
                    word-break: break-word;
                }

                ${scopeSel} .kbola-feedMiniTag {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    height: 24px;
                    padding: 0 9px;
                    border-radius: 999px;
                    background: var(--evt-soft);
                    color: var(--evt-accent);
                    font-size: 10.5px;
                    font-weight: 900;
                    letter-spacing: 0.05em;
                    text-transform: uppercase;
                    flex-shrink: 0;
                }

                ${scopeSel} .kbola-feedLinked {
                    color: rgba(20,21,26,0.62);
                    font-size: 12px;
                    font-weight: 700;
                    word-break: break-word;
                }

                ${scopeSel} .kbola-feedPersonRow {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                }

                ${scopeSel} .kbola-feedPersonIcon {
                    width: 34px;
                    height: 34px;
                    border-radius: 12px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    font-size: 13px;
                }

                ${scopeSel} .kbola-feedPersonIcon.out {
                    background: rgba(220,53,69,0.10);
                    color: ${GlobalStyles.colors.danger};
                }

                ${scopeSel} .kbola-feedPersonIcon.in {
                    background: rgba(25,135,84,0.10);
                    color: ${GlobalStyles.colors.success};
                }

                ${scopeSel} .kbola-feedPersonText {
                    min-width: 0;
                }

                ${scopeSel} .kbola-feedPrimaryText {
                    color: rgba(20,21,26,0.94);
                    font-size: 14px;
                    font-weight: 800;
                    line-height: 1.28;
                    word-break: break-word;
                }

                ${scopeSel} .kbola-feedSecondaryText {
                    color: rgba(20,21,26,0.52);
                    font-size: 11px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    margin-top: 2px;
                }

                ${scopeSel} .kbola-feedNotes {
                    margin-top: 12px;
                    padding-top: 10px;
                    border-top: 1px dashed rgba(0,0,0,0.10);
                    color: rgba(20,21,26,0.62);
                    font-size: 12px;
                    font-weight: 600;
                    word-break: break-word;
                }
            `}</style>
        </>
    );
}

export default function SoccerGameDetailsModal({
                                                   open,
                                                   onClose,
                                                   gameId,
                                                   teamId,
                                                   homeTeam,
                                                   visitorTeam,
                                                   scoreHome,
                                                   scoreVisitor,
                                                   penaltiesHome,
                                                   penaltiesVisitor,
                                                   canEdit,
                                               }) {
    const {t} = useTranslation();
    const colors = useThemeColors();

    const defaultTeamId = String(teamId || homeTeam?.id || visitorTeam?.id || "");
    const [form, setForm] = useState({...INITIAL_FORM, teamId: defaultTeamId});
    const [saving, setSaving] = useState(false);
    const [mounted, setMounted] = useState(false);

    const scopeSel = useMemo(
        () => `#soccer-game-details-modal-${gameId || "x"}`,
        [gameId]
    );

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        const nextDefault = String(teamId || homeTeam?.id || visitorTeam?.id || "");
        if (open) {
            setForm({...INITIAL_FORM, teamId: nextDefault});
            document.body.classList.add("modal-open");
        } else {
            document.body.classList.remove("modal-open");
        }

        return () => {
            document.body.classList.remove("modal-open");
        };
    }, [open, teamId, homeTeam?.id, visitorTeam?.id]);

    const selectedRosterTeamId = form.teamId || defaultTeamId;
    const {
        data: timelineData,
        loading,
        error,
        refetch,
    } = useQuery(GET_SOCCER_GAME_TIMELINE, {
        variables: {
            gameId: parseInt(gameId, 10),
            teamId: parseInt(selectedRosterTeamId, 10),
        },
        skip: !open || !gameId || !selectedRosterTeamId,
        fetchPolicy: "cache-and-network",
    });

    const [saveSoccerPlayerScore] = useMutation(SAVE_SOCCER_PLAYER_SCORE);
    const [deleteSoccerPlayerScore] = useMutation(DELETE_SOCCER_PLAYER_SCORE);

    const eventTypeStyle = useMemo(() => getEventConfig(t), [t]);

    const eventTypeOptions = useMemo(
        () =>
            EVENT_TYPES.map((item) => ({
                value: item.key,
                label: t(`gameElement.soccerGameSheet.eventTypes.${item.key}`),
            })),
        [t]
    );

    const teamOptions = useMemo(() => {
        const options = [];
        if (visitorTeam?.id) {
            options.push({
                value: String(visitorTeam.id),
                label: visitorTeam?.name || "Visitor",
            });
        }
        if (homeTeam?.id) {
            options.push({
                value: String(homeTeam.id),
                label: homeTeam?.name || "Home",
            });
        }
        return options;
    }, [visitorTeam?.id, visitorTeam?.name, homeTeam?.id, homeTeam?.name]);

    const players = useMemo(() => {
        return (timelineData?.getTeamRosterForLineup || []).map((p) => ({
            key: String(p?.player?.id ?? ""),
            value: p?.player?.fullName || "",
        }));
    }, [timelineData]);

    const selectedTeamName = useMemo(() => {
        const found = teamOptions.find((item) => item.value === String(selectedRosterTeamId));
        return found?.label || "";
    }, [teamOptions, selectedRosterTeamId]);

    const timelineEvents = useMemo(() => {
        return (timelineData?.soccerPlayerScoresByGame || [])
            .map((event) => ({
                ...event,
                side: String(event?.team?.id) === String(homeTeam?.id) ? "left" : "right",
            }))
            .sort((a, b) => {
                const am = Number(a?.minute ?? 0);
                const bm = Number(b?.minute ?? 0);

                if (bm !== am) return bm - am;

                const at = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
                const bt = b?.timestamp ? new Date(b.timestamp).getTime() : 0;

                return bt - at;
            });
    }, [timelineData, homeTeam?.id]);

    const scores = useMemo(() => {
        let visitorScore = 0;
        let homeScore = 0;

        (timelineData?.soccerPlayerScoresByGame || []).forEach((event) => {
            const isHomeTeam = String(event?.team?.id) === String(homeTeam?.id);
            const actionKey = String(event?.actionType || "").toLowerCase();

            if (actionKey === "goal") {
                if (isHomeTeam) {
                    homeScore++;
                } else {
                    visitorScore++;
                }
            } else if (actionKey === "own_goal") {
                if (isHomeTeam) {
                    visitorScore++;
                } else {
                    homeScore++;
                }
            }
        });

        return {
            visitorScore: scoreVisitor ?? visitorScore,
            homeScore: scoreHome ?? homeScore,
        };
    }, [scoreHome, scoreVisitor, timelineData, homeTeam?.id]);

    const updateForm = (key, value) => {
        setForm((prev) => {
            const next = {...prev, [key]: value};
            if (key === "teamId") {
                next.playerId = "";
                next.relatedPlayerId = "";
            }
            return next;
        });
    };

    const handleSaveEvent = async (e) => {
        if (e?.preventDefault) e.preventDefault();

        if (!form.teamId || !form.actionType || !form.playerId || !form.minute) {
            toast.warning(t("gameElement.soccerGameSheet.validation.required"));
            return;
        }

        setSaving(true);

        try {
            const input = {
                gameId,
                teamId: form.teamId,
                playerId: form.playerId,
                actionType: form.actionType,
                minute: parseInt(form.minute, 10),
                period: form.period || null,
                notes: form.notes || "",
                relatedPlayerId: form.relatedPlayerId || null,
            };

            const resp = await saveSoccerPlayerScore({variables: {input}});

            if (!resp?.data?.saveSoccerPlayerScore?.score) {
                toast.error(t("gameElement.soccerGameSheet.toasts.saveFailed"));
                return;
            }

            toast.success(t("gameElement.soccerGameSheet.toasts.saveSuccess"));
            setForm((prev) => ({
                ...INITIAL_FORM,
                teamId: prev.teamId,
            }));
            await refetch();

            setTimeout(() => {
                const container = document.querySelector(`${scopeSel} .kbola-centralTimeline`);
                if (container) {
                    container.scrollTop = 0;
                }
            }, 100);
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "gameElement.soccerGameSheet.toasts.saveFailed"));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (scoreId) => {
        try {
            await deleteSoccerPlayerScore({variables: {scoreId}});
            toast.success(t("gameElement.soccerGameSheet.toasts.deleteSuccess"));
            await refetch();
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"));
        }
    };

    if (!open || !mounted) return null;

    const modalNode = (
        <>
            <div
                className="modal fade show kbola-soccerModal"
                id={`soccer-game-details-modal-${gameId || "x"}`}
                style={{display: "block"}}
                tabIndex="-1"
                role="dialog"
                aria-labelledby={`soccerGameDetailsTitle-${gameId || "x"}`}
                aria-modal="true"
            >
                <div className="modal-dialog modal-xl" role="document">
                    <div className="modal-content kbola-modalContent">
                        <div className="modal-header kbola-modalHeader">
                            <div className="d-flex align-items-center" style={{gap: 10, minWidth: 0}}>
                                <span className="kbola-pill kbola-pillBrand">
                                    {t("gameElement.enterDetails")}
                                </span>
                                <h4
                                    className="modal-title mb-0 kbola-modalTitle"
                                    id={`soccerGameDetailsTitle-${gameId || "x"}`}
                                >
                                    {t("gameElement.soccerGameSheet.title")}
                                </h4>
                            </div>

                            <button
                                type="button"
                                className="close kbola-closeBtn"
                                onClick={onClose}
                                aria-label={t("common.cancel")}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        <div className="modal-body kbola-modalBody">
                            <GameMatchboard
                                visitorTeam={visitorTeam}
                                homeTeam={homeTeam}
                                visitorScore={scores.visitorScore}
                                homeScore={scores.homeScore}
                                visitorLabel={t("gameElement.soccerGameSheet.visitorShort", {}, "VISITOR")}
                                homeLabel={t("gameElement.soccerGameSheet.homeShort", {}, "HOME")}
                                penaltiesHome={penaltiesHome}
                                penaltiesVisitor={penaltiesVisitor}
                                penaltyLabel={t("gameResultModal.labels.penaltyShootout")}
                            />

                                <div className={`kbola-soccerShell ${!canEdit ? "is-view-only" : ""}`}>
                                    <div className="kbola-soccerTimelinePanel">
                                        <div className="kbola-centralTimelineHeader">
                                            <span className="kbola-centralTimelineLabel is-left">
                                                {homeTeam?.name || "Home"}
                                            </span>
                                            <span className="kbola-centralTimelineLabel is-center">
                                                {t("gameElement.soccerGameSheet.timeline")}
                                            </span>
                                            <span className="kbola-centralTimelineLabel is-right">
                                                {visitorTeam?.name || "Visitor"}
                                            </span>
                                        </div>

                                        {loading ? (
                                            <div className="kbola-soccerEmpty">
                                                <i className="fas fa-spinner fa-spin mr-2"/>
                                                {t("common.loading")}
                                            </div>
                                        ) : error ? (
                                            <div className="kbola-soccerEmpty">
                                                {getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}
                                            </div>
                                        ) : timelineEvents.length === 0 ? (
                                            <div className="kbola-soccerEmpty">
                                                {t("gameElement.soccerGameSheet.noEvents")}
                                            </div>
                                        ) : (
                                            <div className="kbola-centralTimeline">
                                                <div className="kbola-centralTimelineRail"/>

                                                {timelineEvents.map((event) => {
                                                    const actionKey = String(event?.actionType || "").toLowerCase();
                                                    const cfg = eventTypeStyle[actionKey] || {};
                                                    const side = event.side === "right" ? "right" : "left";

                                                    return (
                                                        <div
                                                            key={event.id}
                                                            className={`kbola-centralTimelineRow ${side === "right" ? "is-right" : "is-left"}`}
                                                        >
                                                            <div className="kbola-centralTimelineSide left">
                                                                {side === "left" ? (
                                                                    <TimelineEventCard
                                                                        event={event}
                                                                        cfg={cfg}
                                                                        canEdit={canEdit}
                                                                        onDelete={handleDelete}
                                                                        side="left"
                                                                        scopeSel={scopeSel}
                                                                    />
                                                                ) : null}
                                                            </div>

                                                            <div className="kbola-centralTimelineMiddle">
                                                                <div
                                                                    className="kbola-centralMinute"
                                                                    style={{
                                                                        "--evt-accent": cfg?.accent || colors.primary,
                                                                        "--evt-glow": cfg?.glow || "rgba(0,0,0,0.06)",
                                                                    }}
                                                                >
                                                                    {event?.minute}'
                                                                </div>
                                                            </div>

                                                            <div className="kbola-centralTimelineSide right">
                                                                {side === "right" ? (
                                                                    <TimelineEventCard
                                                                        event={event}
                                                                        cfg={cfg}
                                                                        canEdit={canEdit}
                                                                        onDelete={handleDelete}
                                                                        side="right"
                                                                        scopeSel={scopeSel}
                                                                    />
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {canEdit && (
                                        <>
                                            <div className="kbola-soccerFormPanel">
                                                <div className="kbola-soccerFormHeader">
                                                    <div className="kbola-soccerFormBadge">
                                                        <i className="fas fa-plus mr-2"/>
                                                        {t("gameElement.enterDetails")}
                                                    </div>
                                                    <div className="kbola-soccerFormTitle">
                                                        {t("gameElement.soccerGameSheet.title")}
                                                    </div>
                                                    {selectedTeamName ? (
                                                        <div className="kbola-soccerFormSubTitle">
                                                            {selectedTeamName}
                                                        </div>
                                                    ) : null}
                                                </div>

                                                <form onSubmit={handleSaveEvent}>
                                                    <div className="kbola-soccerFormGrid">
                                                        <div className="kbola-soccerField">
                                                            <label className="kbola-soccerLabel">
                                                                {t("gameElement.soccerGameSheet.fields.team", {defaultValue: "Team"})}
                                                            </label>
                                                            <select
                                                                className="form-control kbola-soccerControl"
                                                                value={form.teamId}
                                                                onChange={(e) => updateForm("teamId", e.target.value)}
                                                            >
                                                                <option value="">
                                                                    {t("gameElement.soccerGameSheet.select")}
                                                                </option>
                                                                {teamOptions.map((item) => (
                                                                    <option key={item.value} value={item.value}>
                                                                        {item.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="kbola-soccerField">
                                                            <label className="kbola-soccerLabel">
                                                                {t("gameElement.soccerGameSheet.fields.eventType")}
                                                            </label>
                                                            <select
                                                                className="form-control kbola-soccerControl"
                                                                value={form.actionType}
                                                                onChange={(e) => updateForm("actionType", e.target.value)}
                                                            >
                                                                <option value="">
                                                                    {t("gameElement.soccerGameSheet.select")}
                                                                </option>
                                                                {eventTypeOptions.map((item) => (
                                                                    <option key={item.value} value={item.value}>
                                                                        {item.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="kbola-soccerField">
                                                            <label className="kbola-soccerLabel">
                                                                {t("gameElement.soccerGameSheet.fields.player")}
                                                            </label>
                                                            <select
                                                                className="form-control kbola-soccerControl"
                                                                value={form.playerId}
                                                                onChange={(e) => updateForm("playerId", e.target.value)}
                                                            >
                                                                <option value="">
                                                                    {t("gameElement.soccerGameSheet.select")}
                                                                </option>
                                                                {players.map((item) => (
                                                                    <option key={item.key} value={item.key}>
                                                                        {item.value}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="kbola-soccerField">
                                                            <label className="kbola-soccerLabel">
                                                                {t("gameElement.soccerGameSheet.fields.relatedPlayer")}
                                                            </label>
                                                            <select
                                                                className="form-control kbola-soccerControl"
                                                                value={form.relatedPlayerId}
                                                                onChange={(e) => updateForm("relatedPlayerId", e.target.value)}
                                                            >
                                                                <option value="">
                                                                    {t("gameElement.soccerGameSheet.select")}
                                                                </option>
                                                                {players.map((item) => (
                                                                    <option key={item.key} value={item.key}>
                                                                        {item.value}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="kbola-soccerField">
                                                            <label className="kbola-soccerLabel">
                                                                {t("gameElement.soccerGameSheet.fields.minute")}
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                className="form-control kbola-soccerControl"
                                                                value={form.minute}
                                                                onChange={(e) => updateForm("minute", e.target.value)}
                                                                placeholder={t("gameElement.soccerGameSheet.fields.minute")}
                                                            />
                                                        </div>

                                                        <div className="kbola-soccerField">
                                                            <label className="kbola-soccerLabel">
                                                                {t("gameElement.soccerGameSheet.fields.period")}
                                                            </label>
                                                            <select
                                                                className="form-control kbola-soccerControl"
                                                                value={form.period}
                                                                onChange={(e) => updateForm("period", e.target.value)}
                                                            >
                                                                <option value="">
                                                                    {t("gameElement.soccerGameSheet.select")}
                                                                </option>
                                                                {PERIODS.map((item) => (
                                                                    <option key={item.key} value={item.key}>
                                                                        {item.value}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="kbola-soccerField kbola-soccerFieldFull">
                                                            <label className="kbola-soccerLabel">
                                                                {t("gameElement.soccerGameSheet.fields.notes")}
                                                            </label>
                                                            <textarea
                                                                rows={4}
                                                                className="form-control kbola-soccerControl kbola-soccerTextarea"
                                                                value={form.notes}
                                                                onChange={(e) => updateForm("notes", e.target.value)}
                                                                placeholder={t("gameElement.soccerGameSheet.fields.notes")}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="kbola-soccerFormFooter">
                                                        <button
                                                            type="button"
                                                            className="btn btn-default kbola-secondaryBtn"
                                                            onClick={onClose}
                                                        >
                                                            {t("common.cancel")}
                                                        </button>

                                                        <button
                                                            type="submit"
                                                            className="btn btn-kbola kbola-primaryBtn"
                                                            disabled={saving}
                                                        >
                                                            {saving ? (
                                                                <>
                                                            <span
                                                                className="spinner-border spinner-border-sm mr-2"
                                                                role="status"
                                                                aria-hidden="true"
                                                            />
                                                                    {t("common.loading")}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="fas fa-save mr-2"/>
                                                                    {t("common.save")}
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </>)}
                                </div>
                        </div>
                    </div>
                </div>

                <div className="modal-backdrop fade show" onClick={onClose}/>

                <style>{`
                    ${scopeSel} {
                        font-family: 'Poppins', sans-serif;
                        z-index: 1055;
                    }

                    ${scopeSel} i.fas,
                    ${scopeSel} i.far,
                    ${scopeSel} i.fal,
                    ${scopeSel} i.fa,
                    ${scopeSel} i[class^="fa-"],
                    ${scopeSel} i[class*=" fa-"] {
                        font-family: "Font Awesome 5 Free" !important;
                        font-weight: 900;
                    }
                    ${scopeSel} i.fab {
                        font-family: "Font Awesome 5 Brands" !important;
                        font-weight: 400;
                    }

                    ${scopeSel}.modal {
                        overflow-y: auto;
                    }

                    ${scopeSel} .modal-dialog {
                        margin: 1.75rem auto;
                        max-width: 1260px;
                    }

                    ${scopeSel} .kbola-modalContent {
                        position: relative;
                        z-index: 1060;
                        border-radius: 20px;
                        overflow: hidden;
                        border: 1px solid rgba(0,0,0,0.08);
                        box-shadow: 0 22px 60px rgba(0,0,0,0.18);
                    }

                    ${scopeSel} .kbola-modalHeader {
                        padding: 14px 16px;
                        background: ${colors.primary};
                        border-bottom: 1px solid rgba(0,0,0,0.06);
                    }

                    ${scopeSel} .kbola-modalTitle {
                        font-size: 16px;
                        font-weight: 900;
                        color: ${colors.textOnPrimary};
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    ${scopeSel} .kbola-closeBtn {
                        color: ${colors.textOnPrimary};
                        opacity: 1;
                    }

                    ${scopeSel} .kbola-modalBody {
                        background:
                            radial-gradient(circle at top left, rgba(var(--kbs-primary-rgb, 0,173,255), 0.05), transparent 26%),
                            linear-gradient(180deg, #fbfbfc 0%, #f6f7f9 100%);
                        max-height: calc(100vh - 160px);
                        overflow-y: auto;
                        padding: 18px;
                    }

                    ${scopeSel} .kbola-pill {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 6px 10px;
                        border-radius: 999px;
                        font-weight: 900;
                        font-size: 12px;
                        border: 1px solid rgba(0,0,0,0.10);
                        line-height: 1;
                        white-space: nowrap;
                    }

                    ${scopeSel} .kbola-pillBrand {
                        background: ${colors.textOnPrimary};
                        color: ${colors.primary};
                        border-color: rgba(0,0,0,0.10);
                    }

                    ${scopeSel} .kbola-primaryBtn {
                        border-radius: 12px;
                        font-weight: 900;
                        padding: 10px 14px;
                        background: ${colors.primary};
                        border: 1px solid rgba(0,0,0,0.10);
                        color: ${colors.textOnPrimary};
                    }

                    ${scopeSel} .kbola-secondaryBtn {
                        border-radius: 12px;
                        font-weight: 900;
                        padding: 10px 14px;
                    }

                    ${scopeSel} .kbola-soccerShell {
                        display: grid;
                        grid-template-columns: minmax(0, 1.8fr) minmax(360px, 0.88fr);
                        gap: 16px;
                        align-items: start;
                    }

                    ${scopeSel} .kbola-soccerShell.is-view-only {
                        grid-template-columns: 1fr;
                    }

                    ${scopeSel} .kbola-soccerTimelinePanel,
                    ${scopeSel} .kbola-soccerFormPanel {
                        border-radius: 22px;
                        border: 1px solid rgba(0,0,0,0.07);
                        background: rgba(255,255,255,0.92);
                        box-shadow: 0 12px 30px rgba(0,0,0,0.05);
                        backdrop-filter: blur(6px);
                    }

                    ${scopeSel} .kbola-soccerTimelinePanel {
                        overflow: hidden;
                        padding: 16px;
                    }

                    ${scopeSel} .kbola-centralTimelineHeader {
                        display: grid;
                        grid-template-columns: 1fr 92px 1fr;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 12px;
                    }

                    ${scopeSel} .kbola-centralTimelineLabel {
                        font-size: 11px;
                        font-weight: 900;
                        text-transform: uppercase;
                        letter-spacing: 0.08em;
                        color: rgba(20,21,26,0.48);
                    }

                    ${scopeSel} .kbola-centralTimelineLabel.is-center {
                        text-align: center;
                    }

                    ${scopeSel} .kbola-centralTimelineLabel.is-right {
                        text-align: right;
                    }

                    ${scopeSel} .kbola-centralTimeline {
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        gap: 18px;
                    }

                    ${scopeSel} .kbola-centralTimelineRail {
                        position: absolute;
                        left: 50%;
                        top: 0;
                        bottom: 0;
                        width: 2px;
                        transform: translateX(-50%);
                        background:
                            linear-gradient(
                                180deg,
                                rgba(0,0,0,0.03) 0%,
                                rgba(0,0,0,0.10) 12%,
                                rgba(0,0,0,0.10) 88%,
                                rgba(0,0,0,0.03) 100%
                            );
                    }

                    ${scopeSel} .kbola-centralTimelineRow {
                        position: relative;
                        display: grid;
                        grid-template-columns: minmax(0, 1fr) 92px minmax(0, 1fr);
                        gap: 10px;
                        align-items: center;
                    }

                    ${scopeSel} .kbola-centralTimelineSide.left {
                        display: flex;
                        justify-content: flex-end;
                    }

                    ${scopeSel} .kbola-centralTimelineSide.right {
                        display: flex;
                        justify-content: flex-start;
                    }

                    ${scopeSel} .kbola-centralTimelineMiddle {
                        position: relative;
                        z-index: 2;
                        display: flex;
                        justify-content: center;
                    }

                    ${scopeSel} .kbola-centralMinute {
                        min-width: 56px;
                        height: 56px;
                        border-radius: 999px;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        background: #fff;
                        color: rgba(20,21,26,0.88);
                        font-size: 13px;
                        font-weight: 900;
                        box-shadow:
                            0 8px 18px rgba(0,0,0,0.06),
                            0 0 0 6px #fff,
                            0 0 0 8px var(--evt-glow);
                        border: 2px solid var(--evt-accent);
                    }

                    ${scopeSel} .kbola-soccerEmpty {
                        min-height: 180px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 18px;
                        border: 1px dashed rgba(0,0,0,0.11);
                        background: rgba(0,0,0,0.02);
                        color: rgba(20,21,26,0.60);
                        font-size: 13px;
                        font-weight: 800;
                        text-align: center;
                        padding: 16px;
                    }

                    ${scopeSel} .kbola-soccerFormPanel {
                        padding: 16px;
                        position: sticky;
                        top: 0;
                    }

                    ${scopeSel} .kbola-soccerFormHeader {
                        margin-bottom: 14px;
                    }

                    ${scopeSel} .kbola-soccerFormBadge {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 6px 10px;
                        border-radius: 999px;
                        background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                        color: rgba(20,21,26,0.82);
                        border: 1px solid rgba(0,0,0,0.08);
                        font-size: 12px;
                        font-weight: 900;
                        line-height: 1;
                        margin-bottom: 10px;
                    }

                    ${scopeSel} .kbola-soccerFormTitle {
                        font-size: 18px;
                        font-weight: 900;
                        color: rgba(20,21,26,0.94);
                    }

                    ${scopeSel} .kbola-soccerFormSubTitle {
                        margin-top: 4px;
                        font-size: 12px;
                        font-weight: 800;
                        color: rgba(20,21,26,0.52);
                        text-transform: uppercase;
                        letter-spacing: 0.04em;
                    }

                    ${scopeSel} .kbola-soccerFormGrid {
                        display: grid;
                        grid-template-columns: 1fr;
                        gap: 14px;
                    }

                    ${scopeSel} .kbola-soccerField {
                        display: flex;
                        flex-direction: column;
                    }

                    ${scopeSel} .kbola-soccerFieldFull {
                        grid-column: 1 / -1;
                    }

                    ${scopeSel} .kbola-soccerLabel {
                        margin-bottom: 8px;
                        font-size: 13px;
                        font-weight: 900;
                        color: rgba(20,21,26,0.72);
                    }

                    ${scopeSel} .kbola-soccerControl {
                        border-radius: 13px;
                        border: 1px solid rgba(0,0,0,0.10);
                        font-weight: 800;
                        font-size: 13px;
                        padding: 10px 10px;
                        min-height: 44px;
                        box-shadow: none;
                        background: rgba(255,255,255,0.96);
                    }

                    ${scopeSel} .kbola-soccerControl:focus {
                        outline: none;
                        box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                        border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.75);
                    }

                    ${scopeSel} .kbola-soccerTextarea {
                        min-height: 116px;
                        resize: vertical;
                    }

                    ${scopeSel} .kbola-soccerFormFooter {
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        gap: 10px;
                        margin-top: 16px;
                        padding-top: 14px;
                        border-top: 1px solid rgba(0,0,0,0.06);
                    }

                    ${scopeSel} .modal-backdrop {
                        z-index: 1050;
                    }

                    @media (max-width: 1199px) {
                        ${scopeSel} .kbola-soccerShell {
                            grid-template-columns: 1fr;
                        }

                        ${scopeSel} .kbola-soccerFormPanel {
                            position: static;
                        }
                    }

                    @media (max-width: 900px) {
                        ${scopeSel} .kbola-centralTimelineHeader {
                            grid-template-columns: 1fr;
                            gap: 4px;
                        }

                        ${scopeSel} .kbola-centralTimelineLabel.is-center {
                            display: none;
                        }

                        ${scopeSel} .kbola-centralTimelineLabel.is-right,
                        ${scopeSel} .kbola-centralTimelineLabel.is-left {
                            text-align: left;
                        }

                        ${scopeSel} .kbola-centralTimelineRail {
                            left: 50%;
                            transform: translateX(-50%);
                        }

                        ${scopeSel} .kbola-centralTimelineRow {
                            grid-template-columns: 1fr;
                            gap: 0;
                            position: relative;
                            min-height: 88px;
                        }

                        ${scopeSel} .kbola-centralTimelineMiddle {
                            position: absolute;
                            left: 50%;
                            top: 50%;
                            transform: translate(-50%, -50%);
                            z-index: 3;
                            justify-content: center;
                        }

                        ${scopeSel} .kbola-centralTimelineSide.left,
                        ${scopeSel} .kbola-centralTimelineSide.right {
                            width: 100%;
                            display: flex;
                        }

                        ${scopeSel} .kbola-centralTimelineRow.is-left .kbola-centralTimelineSide.left {
                            justify-content: flex-start;
                            padding-right: 52%;
                        }

                        ${scopeSel} .kbola-centralTimelineRow.is-right .kbola-centralTimelineSide.right {
                            justify-content: flex-end;
                            padding-left: 52%;
                        }

                        ${scopeSel} .kbola-centralTimelineRow.is-left .kbola-feedCard {
                            margin-right: auto;
                        }

                        ${scopeSel} .kbola-centralTimelineRow.is-right .kbola-feedCard {
                            margin-left: auto;
                        }

                        ${scopeSel} .kbola-feedCard {
                            width: calc(50% - 22px);
                            min-width: 0;
                        }

                        ${scopeSel} .kbola-centralMinute {
                            min-width: 46px;
                            height: 46px;
                            font-size: 12px;
                            box-shadow:
                                0 6px 14px rgba(0,0,0,0.05),
                                0 0 0 4px #fff,
                                0 0 0 6px var(--evt-glow);
                        }

                        ${scopeSel} .kbola-feedTeamTag {
                            margin-bottom: 8px;
                        }
                    }

                    @media (max-width: 767px) {
                        ${scopeSel} .kbola-centralTimelineRail {
                            left: 26px;
                            transform: none;
                        }

                        ${scopeSel} .kbola-centralTimelineMiddle {
                            left: 26px;
                            transform: translate(-50%, -50%);
                        }

                        ${scopeSel} .kbola-centralTimelineRow.is-left .kbola-centralTimelineSide.left,
                        ${scopeSel} .kbola-centralTimelineRow.is-right .kbola-centralTimelineSide.right {
                            padding-left: 64px;
                            padding-right: 0;
                            justify-content: flex-start;
                        }

                        ${scopeSel} .kbola-feedCard {
                            width: 100%;
                            max-width: none;
                        }

                        ${scopeSel} .kbola-feedTeamTag {
                            margin-bottom: 8px;
                        }

                        ${scopeSel} .kbola-soccerFormFooter {
                            flex-direction: column-reverse;
                        }

                        ${scopeSel} .kbola-soccerFormFooter .btn {
                            width: 100%;
                        }
                    }
                `}</style>
            </div>
        </>
    );

    return createPortal(modalNode, document.body);
}
